// Small IndexedDB helper to persist uploaded files across steps without
// storing large blobs in atoms/localStorage. Runs only in the browser.

const DB_NAME = 'reportCaseFilesDb';
const STORE_NAME = 'files';

type StoredFileRecord = {
  id: string;
  name: string;
  type: string;
  size: number;
  lastModified: number;
  blob: Blob;
};

const isBrowser = typeof window !== 'undefined';

function openDb(): Promise<IDBDatabase> {
  if (!isBrowser) return Promise.reject(new Error('IndexedDB not available'));

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveFilesToIdb(files: File[]): Promise<string[]> {
  if (!isBrowser || files.length === 0) return [];
  const db = await openDb();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  const ids: string[] = [];
  await Promise.all(
    files.map(
      (file) =>
        new Promise<void>((resolve, reject) => {
          const id = crypto.randomUUID();
          const record: StoredFileRecord = {
            id,
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
            blob: file,
          };
          const req = store.put(record);
          req.onsuccess = () => {
            ids.push(id);
            resolve();
          };
          req.onerror = () => reject(req.error);
        }),
    ),
  );

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error as DOMException);
    tx.onabort = () => reject(tx.error as DOMException);
  });

  db.close();
  return ids;
}

export async function getFilesFromIdb(ids: string[]): Promise<File[]> {
  if (!isBrowser || ids.length === 0) return [];
  const db = await openDb();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);

  const files: File[] = [];
  await Promise.all(
    ids.map(
      (id) =>
        new Promise<void>((resolve, reject) => {
          const req = store.get(id);
          req.onsuccess = () => {
            const rec = req.result as StoredFileRecord | undefined;
            if (rec) {
              const file = new File([rec.blob], rec.name, {
                type: rec.type,
                lastModified: rec.lastModified,
              });
              files.push(file);
            }
            resolve();
          };
          req.onerror = () => reject(req.error);
        }),
    ),
  );

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error as DOMException);
    tx.onabort = () => reject(tx.error as DOMException);
  });

  db.close();
  return files;
}

export async function getFileNamesFromIdb(ids: string[]): Promise<string[]> {
  if (!isBrowser || ids.length === 0) return [];
  const db = await openDb();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);

  const names: string[] = [];
  await Promise.all(
    ids.map(
      (id) =>
        new Promise<void>((resolve, reject) => {
          const req = store.get(id);
          req.onsuccess = () => {
            const rec = req.result as StoredFileRecord | undefined;
            if (rec) names.push(rec.name);
            resolve();
          };
          req.onerror = () => reject(req.error);
        }),
    ),
  );

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error as DOMException);
    tx.onabort = () => reject(tx.error as DOMException);
  });

  db.close();
  return names;
}

export async function deleteFilesFromIdb(ids: string[]): Promise<void> {
  if (!isBrowser || ids.length === 0) return;
  const db = await openDb();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  await Promise.all(
    ids.map(
      (id) =>
        new Promise<void>((resolve, reject) => {
          const req = store.delete(id);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        }),
    ),
  );

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error as DOMException);
    tx.onabort = () => reject(tx.error as DOMException);
  });

  db.close();
}

export async function clearAllFilesFromIdb(): Promise<void> {
  if (!isBrowser) return;
  const db = await openDb();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  await new Promise<void>((resolve, reject) => {
    const req = store.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error as DOMException);
    tx.onabort = () => reject(tx.error as DOMException);
  });

  db.close();
}

/**
 * Get a FormData object with the files from the IndexedDB
 *  - pulls blobs from IndexedDB
 *  - creates a new FormData
 *  - appends each file under 'files'
 * @param ids - array of file IDs
 * @returns FormData object with the files
 */
export async function getFormDataFromIds(ids: string[]): Promise<FormData> {
  const formData = new FormData();
  const files = await getFilesFromIdb(ids);
  files.forEach((file) => formData.append('files', file));
  return formData;
}
